import React, { useEffect, useMemo } from 'react';
import { AlertBox, Box, FlexBox } from '@vp/swan';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import { DateFormatTypes } from '@vp/om-date-formatter';
import useSiteData from '../../../hooks/useSiteData';
import { useLineItemData } from '../../contexts/LineItemDataProvider';
import Date from '../../shared/Date';
import { EmbeddableLinkCreator } from '../../shared/EmbeddableLinkCreator';
import useIdentityContext from '../../../hooks/useIdentityContext';
import { FeedbackType } from '../../../types/feedback/Feedback';
import { useCustomerFeedbackGroup } from '../../contexts/CustomerFeedbackGroupsProvider';
import {
  trackDesignFeedbackBannerPresented,
  trackDesignFeedbackBannerRedirectLinkClicked,
} from '../../../apis/newRelic/designFeedbackPageNewRelicTracking';
import { FeedbackGroupState } from '../../../types/feedback/FeedbackGroup';

export const DesignFeedbackBanner = () => {
  const [designFeedbackMessages] = useTranslations(['designFeedbackMessages']);

  const { identity } = useIdentityContext();
  const { shopperId } = identity;
  const item = useLineItemData();
  const { orderNumber, id: lineItemId } = item;

  const { externalAppNavigation: nav, vistacartTenant } = useSiteData();
  const orderDetailsUrl = nav.orderDetails;

  const {
    customerFeedbackGroup: pendingDesignFeedbackGroup,
    customerFeedbackGroupExists: pendingDesignFeedbackGroupExists,
  } = useCustomerFeedbackGroup({
    lineItemId,
    requestType: FeedbackType.DOCUMENT_REVIEW,
    groupState: FeedbackGroupState.AWAITING_FEEDBACK,
  });

  const {
    responseDeadlineDate: deadline,
    id: feedbackRequestId,
    groupId,
    state: feedbackGroupState,
  } = pendingDesignFeedbackGroup;

  const designFeedbackPageUrl = `${orderDetailsUrl}?orderId=${orderNumber}&groupId=${groupId}&action=df`;

  const showBanner = pendingDesignFeedbackGroupExists;

  const newRelicTrackingData = useMemo(
    () => ({
      lineItemId,
      orderNumber,
      feedbackGroupState,
      tenant: vistacartTenant,
    }),
    [feedbackGroupState, lineItemId, orderNumber, vistacartTenant]
  );

  // Fire a New Relic metric that the banner was presented to the customer
  useEffect(() => {
    if (showBanner) {
      trackDesignFeedbackBannerPresented(
        shopperId,
        groupId,
        feedbackRequestId,
        newRelicTrackingData
      );
    }
  }, [
    feedbackRequestId,
    groupId,
    feedbackGroupState,
    lineItemId,
    newRelicTrackingData,
    orderNumber,
    shopperId,
    showBanner,
    vistacartTenant,
  ]);

  // On click handler for the redirect link to the design feedback page
  const handleRedirectLinkOnClick = e => {
    // Block the redirect so we can fire the NR metric
    e.preventDefault();

    // Fire a New Relic metric that the link was clicked
    trackDesignFeedbackBannerRedirectLinkClicked(
      shopperId,
      groupId,
      feedbackRequestId,
      newRelicTrackingData
    );

    // Finally, redirect to design feedback page
    window.location.assign(`${designFeedbackPageUrl}`);
  };

  if (showBanner) {
    return (
      <AlertBox skin="error">
        <FlexBox
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box textAlign="left" pr={5}>
            {designFeedbackMessages.designFeedbackTitle}{' '}
            <ReplaceTokens
              tokenizedString={designFeedbackMessages.pleaseProvideFeedback}
              substitutions={{
                DesignFeedbackLink: EmbeddableLinkCreator({
                  href: designFeedbackPageUrl,
                  onClick: handleRedirectLinkOnClick,
                  ariaDescribedBy: `product-name-${lineItemId}`, // Attach the product name to make the link unique and descriptive for assistive tech
                }),
                date: (
                  <Date
                    date={deadline}
                    dateFormatType={DateFormatTypes.MEDIUM_FMT}
                  />
                ),
              }}
            />
          </Box>
        </FlexBox>
      </AlertBox>
    );
  }

  return null;
};
